import { useState } from "react";
import { X, Check } from "lucide-react";
import Button from "../ui/Button";
import { AnimatePresence, MotionDiv } from "../ui/MotionWrapper";

export default function GetAQuoteModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const serviceOptions = [
    "Mobile App Development",
    "Website Development",
    "Custom Software",
    "UI/UX Design",
    "Cloud & Hosting",
    "Maintenance & Support",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const obj = {
        action: "addContactRequest",
        ...formData,
      };

      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/manageCustomer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(obj),
      });

      const data = await response.json();

      if (data.status) {
        setTimeout(() => {
          setIsSubmitting(false);
          setSubmitted(true);
          setTimeout(() => {
            setSubmitted(false);
            setFormData({
              name: "",
              email: "",
              phone: "",
              service: "",
              message: "",
            });
            onClose();
          }, 3000);
        }, 1500);
      }
    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <MotionDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-lg z-[999] flex items-center justify-center p-4"
        >
          <MotionDiv
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] relative overflow-hidden flex flex-col"
          >
            <div className="p-6 flex items-center justify-between border-b border-gray-200">
              <h2 className="text-2xl font-bold text-[#172C5E]">Get a Quote</h2>
              <button
                onClick={handleClose}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-8">
              <AnimatePresence>
                {submitted && (
                  <MotionDiv
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                    className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6 z-20"
                  >
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-5 border-4 border-green-200">
                        <Check className="h-10 w-10 text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
                      <p className="text-gray-600 max-w-sm mx-auto">
                        Your request has been sent successfully. We&apos;ll get back to you shortly.
                      </p>
                    </div>
                  </MotionDiv>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#8339EA]/50 focus:border-[#8339EA] transition-colors duration-300"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Email (Optional)
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#8339EA]/50 focus:border-[#8339EA] transition-colors duration-300"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    required
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#8339EA]/50 focus:border-[#8339EA] transition-colors duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Service of Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-[#8339EA]/50 focus:border-[#8339EA] transition-colors duration-300"
                  >
                    <option value="" disabled>Select a service</option>
                    {serviceOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#8339EA]/50 focus:border-[#8339EA] transition-colors duration-300"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={isSubmitting} size="lg">
                    {isSubmitting ? "Sending..." : "Submit Request"}
                  </Button>
                </div>
              </form>
            </div>
          </MotionDiv>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
}
